import React, { useEffect, useRef, useState } from "react"
import { Chart, registerables } from "chart.js"
import { Spin } from "antd"
import { translations } from "../../utils/Translation/staticTranslation"
import { formatFeatureLabel } from "../../utils/organizationApiHelpers"

// Register all Chart.js components
Chart.register(...registerables)

const FeatureUsageChart = ({ data, loading }) => {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)
  const [iconPositions, setIconPositions] = useState([])

  // 🌍 Detect language
  const lang = localStorage.getItem("language") || "ar"
  const dashboardAdmin = translations[lang]?.DashboardSuperAdmin || {}

  const featureUsage = data?.feature_usage || {}

  // 🧩 Map features with translations
  const featureData = [
    {
      feature:
        lang === "ar"
          ? dashboardAdmin?.FurnitureFinder || "مكتشف الأثاث"
          : "Furniture Finder",
      value: featureUsage.furniture_finder || 0,
      icon: "furniture_finder",
    },
    {
      feature:
        lang === "ar"
          ? dashboardAdmin?.RoomRedesign || "إعادة تصميم الغرفة"
          : "Room Redesign",
      value: featureUsage.room_redesign || 0,
      icon: "home_redesign",
    },
    {
      feature:
        lang === "ar"
          ? dashboardAdmin?.QuickEdits || "تعديلات سريعة"
          : "Quick Edits",
      value: featureUsage.quick_edit || 0,
      icon: "quick_edit",
    },
    {
      feature:
        lang === "ar"
          ? dashboardAdmin?.SketchToRender || "من رسم إلى عرض"
          : "Sketch to Render",
      value: featureUsage.sketch_to_render || 0,
      icon: "sketch_to_render",
    },
    {
      feature:
        lang === "ar"
          ? dashboardAdmin?.TextToImage || "من نص إلى صورة"
          : "Text to Image",
      value: featureUsage.text_to_image || 0,
      icon: "text_to_image",
    },
    {
      feature: lang === "ar" ? "نمط المرجع" : "Reference Style",
      value: featureUsage.reference_style || 0,
      icon: "reference_style",
    },
    {
      feature: lang === "ar" ? "تصميم الجدار" : "Wall Design",
      value: featureUsage.wall_design || 0,
      icon: "wall_design",
    },
    {
      feature: formatFeatureLabel("floor_design", lang),
      value: featureUsage.floor_design || 0,
      icon: "floor_design",
    },
    {
      feature:
        lang === "ar"
          ? dashboardAdmin?.RoomPlanner || "مخطط الغرفة"
          : "Room Planner",
      value: featureUsage.floor_generation || 0,
      icon: "floor_generation",
    },
    {
      feature: lang === "ar" ? "إزالة الأثاث" : "Furniture Removal",
      value: featureUsage.furniture_removal || 0,
      icon: "furniture_removal",
    },
    {
      feature: lang === "ar" ? "استبدال الأثاث" : "Furniture Replace",
      value: featureUsage.furniture_replace || 0,
      icon: "furniture_replace",
    },
    {
      feature: lang === "ar" ? "إعادة ترتيب الأثاث" : "Rearrange Furniture",
      value: featureUsage.rearrange_furniture || 0,
      icon: "rearrange_furniture",
    },
    {
      feature: lang === "ar" ? "تحسين الصورة" : "Enhance Image",
      value: featureUsage.enhance_image || 0,
      icon: "enhance_image",
    },
    {
      feature: lang === "ar" ? "بوث الفعاليات" : "Event Booth",
      value: featureUsage.expert_design || 0,
      icon: "expert_design",
    },
    {
      feature: lang === "ar" ? "التصميم بالمنتجات" : "Design With Product",
      value: featureUsage.design_with_product || 0,
      icon: "design_with_product",
    },
    {
      feature: lang === "ar" ? "استنساخ التصميم" : "Replicate Design",
      value: featureUsage.product_visualizer || 0,
      icon: "product_visualizer",
    },
    {
      feature: formatFeatureLabel("furniture_design", lang),
      value: featureUsage.furniture_design || 0,
      icon: "furniture_design",
    },
  ]

  const getIconComponent = (iconName) => {
    const iconMap = {
      ai_generated: "/dashboard/ai-generated-icon.svg",
      furniture_finder: "/dashboard/sofa-icon.svg",
      home_redesign: "/dashboard/room-redesign-icon.svg",
      object_removal: "/dashboard/object-removal-icon.svg",
      quick_edit: "/dashboard/object-removal-icon.svg",
      sketch_to_render: "/dashboard/quick-edits.svg",
      text_to_image: "/dashboard/credits-icon.svg",
      furniture_removal: "/dashboard/virtua_unstaging.svg",
      furniture_replace: "/dashboard/replace-furniture.svg",
      rearrange_furniture: "/dashboard/rearrange-furniture.svg",
      enhance_image: "/dashboard/image-upload.svg",
      expert_design: "/dashboard/event-booth-icon.svg",
      reference_style: "/dashboard/reference-style-icon.svg",
      wall_design: "/dashboard/wall-design-icon.svg",
      floor_design: "/dashboard/floor-design-icon.svg",
      floor_generation: "/dashboard/floor-generation-icon.svg",
      design_with_product: "/dashboard/design-with-product-icon.svg",
      product_visualizer: "/dashboard/replicate-design-icon.svg",
      furniture_design: "/dashboard/furniture-design-icon.svg",
      default: "/dashboard/default-icon.svg",
    }

    return (
      <div
        style={{
          border: "1px solid #ACD2DC26",
          background: "#F8F8F8",
          width: "30px",
          height: "30px",
          padding: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
        }}
      >
        <img
          src={iconMap[iconName] || iconMap.default}
          alt={iconName}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      </div>
    )
  }

  const renderChart = () => {
    if (!chartRef.current || !featureData.length) return

    const labels = featureData.map((item) => item.feature)
    const values = featureData.map((item) => item.value)
    const total = values.reduce((a, b) => a + b, 0)

    const gradients = [
      "rgba(120, 170, 255, 0.9)",
      "rgba(90, 150, 255, 0.9)",
      "rgba(60, 120, 255, 0.9)",
      "rgba(50, 90, 200, 0.9)",
      "rgba(30, 50, 120, 0.9)",
      "rgba(20, 30, 90, 0.9)",
      "rgba(45, 75, 170, 0.9)",
      "rgba(100, 140, 220, 0.9)",
    ]

    if (chartInstance.current) chartInstance.current.destroy()
    const ctx = chartRef.current.getContext("2d")

    // 🔹 Custom plugin: show text if all values = 0
    const noDataTextPlugin = {
      id: "noDataText",
      afterDraw(chart) {
        if (total > 0) return
        const { ctx, chartArea } = chart
        const cx = (chartArea.left + chartArea.right) / 2
        const cy = (chartArea.top + chartArea.bottom) / 2
        ctx.save()
        ctx.fillStyle = "#9CA3AF"
        ctx.font = "bold 16px Arial"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(
          lang === "ar"
            ? dashboardAdmin?.NoUsageText || "لا يوجد استخدام لهذه الفترة"
            : "No usage for this time period",
          cx,
          cy
        )
        ctx.restore()
      },
    }

    chartInstance.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label:
              lang === "ar"
                ? dashboardAdmin?.UsageCount || "عدد الاستخدامات"
                : "Usage Count",
            data: values,
            backgroundColor: gradients,
            borderRadius: 0,
            barThickness: 60,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: { display: false, drawBorder: false },
            ticks: { display: false },
          },
          y: {
            beginAtZero: true,
            grid: { display: false, drawBorder: false },
            ticks: {
              callback: (value) => (value >= 1000 ? `${value / 1000}K` : value),
              font: { size: 12, weight: "500" },
              color: "#666",
            },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: 12,
            cornerRadius: 8,
            callbacks: {
              label: (context) =>
                lang === "ar"
                  ? `${dashboardAdmin?.Usage || "الاستخدام"}: ${
                      context.parsed.y
                    }`
                  : `Usage: ${context.parsed.y}`,
            },
          },
        },
        layout: { padding: { top: 20, bottom: 60 } },
        animation: {
          onComplete: function () {
            const chart = chartInstance.current
            if (!chart) return
            const meta = chart.getDatasetMeta(0)
            const positions = meta.data.map((bar) => bar.x)
            setIconPositions(positions)
          },
        },
      },
      plugins: [noDataTextPlugin],
    })
  }

  useEffect(() => {
    if (featureData.length > 0 && data) renderChart()
    return () => {
      if (chartInstance.current) chartInstance.current.destroy()
    }
  }, [data, lang])

  return (
    <div className="h-100">
      {loading ? (
        <div className="flex justify-center items-center h-[400px]">
          <Spin size="large" />
        </div>
      ) : (
        <div style={{ position: "relative", width: "100%", height: "400px" }}>
          <canvas ref={chartRef} style={{ height: "400px", width: "100%" }} />
          {/* ✅ Icons below bars */}
          {iconPositions.length > 0 && (
            <div
              style={{
                position: "absolute",
                bottom: "8px",
                left: "0",
                right: "0",
                height: "44px",
              }}
            >
              {featureData.map((item, index) => (
                <div
                  key={index}
                  style={{
                    position: "absolute",
                    left: `${iconPositions[index]}px`,
                    transform: "translateX(-50%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {getIconComponent(item.icon)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default FeatureUsageChart
